import { type StateCreator } from 'zustand';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import { type MultiPolygonG2D } from '@ev-tech/search-modules-api';

import { DEFAULT_SORTING_OPTION_ID } from './optionsSlice';
import { MapMode } from './mapInteractionSlice';
import { usePromptStore } from '../usePromptStore';
import { type MiddlewareType, type SearchStore } from '../SearchStoreProvider';

export type LocationState = {
  listingIds?: string[];
  placeIds?: string[];
  placeName?: string;
  searchRadius: number;
  multiPolygon?: MultiPolygonG2D;
};

type LocationActions = {
  setPlaceIds: (placeIds?: string[]) => void;
  setPlaceName: (placeName?: string) => void;
  setSearchRadius: (searchRadius?: number) => void;
  setMultiPolygon: (multiPolygon?: MultiPolygonG2D, options?: { mapMode?: MapMode }) => void;
};

export type LocationStore = LocationState & LocationActions;

export const defaultLocationState: LocationState = {
  listingIds: undefined,
  placeIds: undefined,
  placeName: undefined,
  searchRadius: 0,
  multiPolygon: undefined,
} as const;

export const createLocationSlice: StateCreator<SearchStore, MiddlewareType, [], LocationStore> = (set, get) => {
  return {
    ...defaultLocationState,
    setPlaceIds: (placeIds?: string[]) => {
      set(
        {
          placeIds,
          searchRadius: 0,
          multiPolygon: undefined,
          mapMode: MapMode.PLACE_MODE,
          page: 1,
          ...(get().sortingOptionId === SortingOptionId.PREFERENCES && { sortingOptionId: DEFAULT_SORTING_OPTION_ID }),
        },
        undefined,
        'setPlaceIds',
      );
    },
    setPlaceName: (placeName?: string) => {
      set(
        {
          placeName,
          mapMode: MapMode.PLACE_MODE,
          ...(get().sortingOptionId === SortingOptionId.PREFERENCES && { sortingOptionId: DEFAULT_SORTING_OPTION_ID }),
        },
        undefined,
        'setPlaceName',
      );
      usePromptStore.getState().setPromptValue('');
    },
    setSearchRadius: (searchRadius?: number) => {
      set(
        {
          searchRadius,
          page: 1,
          ...(get().sortingOptionId === SortingOptionId.PREFERENCES && { sortingOptionId: DEFAULT_SORTING_OPTION_ID }),
        },
        undefined,
        'setSearchRadius',
      );
      usePromptStore.getState().setPromptValue('');
    },
    setMultiPolygon: (multiPolygon?: MultiPolygonG2D, options?: { mapMode?: MapMode }) => {
      set(
        {
          multiPolygon,
          placeIds: undefined,
          placeName: undefined,
          searchRadius: 0,
          page: 1,
          mapMode: options?.mapMode ?? MapMode.MULTI_POLYGON_MODE,
          ...(get().sortingOptionId === SortingOptionId.PREFERENCES && { sortingOptionId: DEFAULT_SORTING_OPTION_ID }),
        },
        undefined,
        'setMultiPolygon',
      );
      usePromptStore.getState().setPromptValue('');
    },
  };
};
